import interact from 'interactjs';
import { DEFAULT_RELEASE_PROFILE, getReleaseDurationMs } from '../../core/drag';
import { DEFAULT_EDGE_BASE, readCssNumber } from '../../core/motion';

export type SharedSheetController = {
    open: () => void;
    close: (immediate?: boolean) => void;
    isOpen: () => boolean;
};

type SharedSheetOptions = {
    root: HTMLElement;
    trigger?: HTMLElement;
    durationMs?: number;
    panel?: HTMLElement;
    backdrop?: HTMLElement;
    onBeforeOpen?: () => void;
    onAfterClose?: () => void;
};

export function createSharedSheetController(options: SharedSheetOptions): SharedSheetController {
    const durationMs = options.durationMs ?? DEFAULT_RELEASE_PROFILE.normal;
    const panel = options.panel;
    const backdrop = options.backdrop;
    const edgeMaxPx = DEFAULT_EDGE_BASE.maxPx;
    const edgeDecay = DEFAULT_EDGE_BASE.decay;

    let dragging = false;
    let translateY = 0;
    let panelHeight = 1;
    let dragStartMs = 0;
    let lastMoveMs = 0;
    let velocity = 0;
    let baseOverlayAlpha = 0;
    let interactFrameId: number | null = null;
    let touchFrameId: number | null = null;

    // touch handoff state for .sheet-content
    let contentTouchActive = false;
    let contentTouchDragActivated = false;
    let contentTouchStartX = 0;
    let contentTouchStartY = 0;
    let contentTouchBaseY = 0;
    let contentTouchLastY = 0;
    let contentTouchLastMs = 0;

    const applyDragFrame = (): void => {
        if (!panel) return;
        panel.style.transform = `translate3d(0, ${translateY}px, 0)`;
        if (backdrop) {
            const progress = (panelHeight - translateY) / Math.max(1, panelHeight);
            const alpha = baseOverlayAlpha * Math.max(0, Math.min(1, progress));
            backdrop.style.setProperty('--overlay-alpha', String(alpha));
        }
    };

    const clearDragFrame = (): void => {
        if (!panel) return;
        panel.style.removeProperty('transform');
        panel.style.removeProperty('transition');
        if (backdrop) {
            backdrop.style.removeProperty('--overlay-alpha');
            backdrop.style.removeProperty('transition');
        }
    };

    const findScrollContentFromTarget = (target: EventTarget | null | undefined): HTMLElement | null => {
        if (target instanceof Element) {
            const content = target.closest('.sheet-content');
            if (content instanceof HTMLElement) return content;
        }
        return null;
    };

    const resolveScrollContent = (event: Interact.DragEvent): HTMLElement | null => {
        const fromDownEvent = findScrollContentFromTarget(
            (event as { interaction?: { downEvent?: { target?: EventTarget | null } } }).interaction?.downEvent?.target,
        );
        if (fromDownEvent) return fromDownEvent;

        const fromEventTarget = findScrollContentFromTarget((event as { target?: EventTarget | null }).target);
        if (fromEventTarget) return fromEventTarget;

        const pointEl = document.elementFromPoint(event.clientX, event.clientY);
        return findScrollContentFromTarget(pointEl);
    };

    const canHandOffToSheetDrag = (content: HTMLElement, dy: number): boolean => {
        const top = content.scrollTop;
        const maxScrollTop = Math.max(0, content.scrollHeight - content.clientHeight);
        const edgeEpsilon = 1;
        if (dy > 0) return top <= edgeEpsilon;
        if (dy < 0) return top >= maxScrollTop - edgeEpsilon;
        return false;
    };

    const finishDrag = (): void => {
        if (!panel) return;
        const releaseDurationMs = getReleaseDurationMs(Math.abs(velocity));
        const returnDurationMs = DEFAULT_RELEASE_PROFILE.normal;

        if (velocity > 0.6) {
            close(false, releaseDurationMs);
            return;
        }

        const closeByDistance = translateY >= Math.max(88, panelHeight * 0.45);
        if (closeByDistance) {
            close(false, durationMs);
            return;
        }

        panel.style.transition = `transform ${returnDurationMs}ms ease`;
        if (backdrop) backdrop.style.transition = `background-color ${returnDurationMs}ms ease`;
        translateY = 0;
        applyDragFrame();
        window.setTimeout(() => {
            clearDragFrame();
        }, returnDurationMs + 20);
    };

    const open = (): void => {
        options.onBeforeOpen?.();
        options.root.hidden = false;
        options.root.setAttribute('aria-hidden', 'false');
        options.root.classList.add('sheet-open');
        options.trigger?.setAttribute('aria-expanded', 'true');
        options.root.dataset.prevOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        panelHeight = panel ? Math.max(1, panel.offsetHeight) : 1;
        translateY = 0;
        clearDragFrame();
        requestAnimationFrame(() => options.root.classList.add('is-open'));
    };

    const close = (immediate = false, closeDurationMs = durationMs): void => {
        options.root.setAttribute('aria-hidden', 'true');
        options.trigger?.setAttribute('aria-expanded', 'false');
        const complete = () => {
            options.root.classList.remove('is-open');
            options.root.classList.remove('sheet-open');
            options.root.hidden = true;
            document.documentElement.style.overflow = options.root.dataset.prevOverflow || '';
            clearDragFrame();
            options.onAfterClose?.();
        };
        if (immediate) {
            complete();
            return;
        }
        if (panel) {
            panel.style.transition = `transform ${closeDurationMs}ms ease`;
            panel.style.transform = `translate3d(0, ${panelHeight}px, 0)`;
        }
        if (backdrop) {
            backdrop.style.transition = `background-color ${closeDurationMs}ms ease`;
            backdrop.style.setProperty('--overlay-alpha', '0');
        }
        window.setTimeout(complete, closeDurationMs);
    };

    const isOpen = (): boolean => !options.root.hidden;

    if (panel) {
        const interactable = interact(panel);
        interactable.preventDefault('never');
        interactable.draggable({
            allowFrom: undefined,
            ignoreFrom: '.no-drag, .sheet-content, .sheet-content *',
            startAxis: 'y',
            lockAxis: 'y',
            listeners: {
                start() {
                    if (!isOpen()) return;
                    dragging = true;
                    panelHeight = Math.max(1, panel.offsetHeight);
                    dragStartMs = performance.now();
                    lastMoveMs = dragStartMs;
                    velocity = 0;
                    panel.style.transition = 'none';
                    if (backdrop) {
                        baseOverlayAlpha = readCssNumber(document.documentElement, '--overlay-alpha-open', 0.4);
                        backdrop.style.transition = 'none';
                    }
                },
                move(event) {
                    if (!isOpen() || !dragging) return;
                    const dy = event.dy;
                    if (dy < 0) {
                        const abs = Math.abs(dy);
                        const resisted = edgeMaxPx * (1 - Math.exp(-abs / Math.max(1, edgeDecay)));
                        translateY = Math.max(-edgeMaxPx, translateY - Math.min(edgeMaxPx, resisted));
                    } else {
                        translateY += dy;
                    }
                    // 瞬間速度（直前フレームとの差分）で計算
                    const now = performance.now();
                    const dt = Math.max(1, now - lastMoveMs);
                    velocity = dy / dt;
                    lastMoveMs = now;
                    // rAF でバッチ描画
                    if (interactFrameId !== null) return;
                    interactFrameId = requestAnimationFrame(() => {
                        interactFrameId = null;
                        applyDragFrame();
                    });
                },
                end() {
                    if (interactFrameId !== null) {
                        cancelAnimationFrame(interactFrameId);
                        interactFrameId = null;
                        applyDragFrame(); // 最終位置を即時反映
                    }
                    if (!isOpen() || !dragging) return;
                    dragging = false;
                    finishDrag();
                },
            },
        });

        const contents = Array.from(panel.querySelectorAll<HTMLElement>('.sheet-content'));
        contents.forEach((content) => {
            content.addEventListener(
                'touchstart',
                (event) => {
                    if (!isOpen()) return;
                    const touch = event.touches[0];
                    if (!touch) return;
                    contentTouchActive = true;
                    contentTouchDragActivated = false;
                    contentTouchStartX = touch.clientX;
                    contentTouchStartY = touch.clientY;
                    contentTouchBaseY = touch.clientY;
                    contentTouchLastY = touch.clientY;
                    contentTouchLastMs = performance.now();
                },
                { passive: true },
            );

            content.addEventListener(
                'touchmove',
                (event) => {
                    if (!isOpen() || !contentTouchActive) return;
                    const touch = event.touches[0];
                    if (!touch) return;

                    const dx = touch.clientX - contentTouchStartX;
                    const dyFromStart = touch.clientY - contentTouchStartY;

                    if (!contentTouchDragActivated) {
                        if (Math.abs(dyFromStart) <= Math.abs(dx) || Math.abs(dyFromStart) < 4) return;
                        if (!canHandOffToSheetDrag(content, dyFromStart)) return;

                        contentTouchDragActivated = true;
                        dragging = true;
                        panelHeight = Math.max(1, panel.offsetHeight);
                        dragStartMs = performance.now();
                        velocity = 0;
                        panel.style.transition = 'none';
                        if (backdrop) {
                            baseOverlayAlpha = readCssNumber(document.documentElement, '--overlay-alpha-open', 0.4);
                            backdrop.style.transition = 'none';
                        }
                        contentTouchBaseY = touch.clientY;
                        contentTouchLastY = touch.clientY;
                        contentTouchLastMs = dragStartMs;
                    }

                    event.preventDefault();
                    const dy = touch.clientY - contentTouchBaseY;
                    if (dy < 0) {
                        const abs = Math.abs(dy);
                        const resisted = edgeMaxPx * (1 - Math.exp(-abs / Math.max(1, edgeDecay)));
                        translateY = -Math.min(edgeMaxPx, resisted);
                    } else {
                        translateY = dy;
                    }

                    const now = performance.now();
                    const dt = Math.max(1, now - contentTouchLastMs);
                    velocity = (touch.clientY - contentTouchLastY) / dt;
                    contentTouchLastY = touch.clientY;
                    contentTouchLastMs = now;
                    // rAF でバッチ描画
                    if (touchFrameId !== null) return;
                    touchFrameId = requestAnimationFrame(() => {
                        touchFrameId = null;
                        applyDragFrame();
                    });
                },
                { passive: false },
            );

            content.addEventListener(
                'touchend',
                () => {
                    if (touchFrameId !== null) {
                        cancelAnimationFrame(touchFrameId);
                        touchFrameId = null;
                        applyDragFrame(); // 最終位置を即時反映
                    }
                    if (!isOpen() || !contentTouchActive) return;
                    contentTouchActive = false;
                    if (!contentTouchDragActivated) return;
                    contentTouchDragActivated = false;
                    dragging = false;
                    finishDrag();
                },
                { passive: true },
            );

            content.addEventListener(
                'touchcancel',
                () => {
                    if (touchFrameId !== null) {
                        cancelAnimationFrame(touchFrameId);
                        touchFrameId = null;
                    }
                    contentTouchActive = false;
                    contentTouchDragActivated = false;
                },
                { passive: true },
            );
        });
    }

    return {
        open,
        close,
        isOpen,
    };
}
